import { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Button,
  FormField,
  Modal,
  TextInput,
  useToast,
} from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useLocale } from "@seller/app/locale";
import { copyTextToClipboard } from "@/lib/browser/clipboard";
import type { BostaConnectionView } from "@/lib/api/types";
import { BostaLogo } from "@/modules/settings/components/integrations/logos/BostaLogo";
import {
  useBostaConnectionQuery,
  useDisconnectBostaMutation,
  useRotateBostaWebhookMutation,
  useSaveBostaApiKeyMutation,
} from "@/modules/settings/services/useBostaConnection";
import { intlLocale } from "@/lib/formatting/locale";

const BOSTA_API_INTEGRATION_URL =
  "https://business.bosta.co/settings/api-integration";
const BOSTA_PICKUP_LOCATIONS_URL =
  "https://business.bosta.co/settings/business-account/create-location";

type Props = {
  storeSlug: string;
  isOpen: boolean;
  onClose: () => void;
};

type T = ReturnType<typeof useTranslations>;

export function BostaSheet({ storeSlug, isOpen, onClose }: Props) {
  const t = useTranslations();
  const { connection, isLoading } = useBostaConnectionQuery({ storeSlug });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      closeLabel={t(keys.common.close)}
      hideFooter
      headerOverride={
        <>
          <h3 className="sr-only">{t(keys.bosta.sheetTitle)}</h3>
          <BostaLogo className="h-7 w-auto object-contain" />
        </>
      }
    >
      {isLoading ? (
        <div className="flex flex-col gap-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-xl bg-[color:var(--surface-alt)]"
            />
          ))}
        </div>
      ) : connection ? (
        <ConnectedView
          storeSlug={storeSlug}
          connection={connection}
          onDisconnected={onClose}
          t={t}
        />
      ) : (
        <SetupView storeSlug={storeSlug} t={t} />
      )}
    </Modal>
  );
}

/* ------------------------------- Setup view ------------------------------ */

function SetupView({ storeSlug, t }: { storeSlug: string; t: T }) {
  const toast = useToast();
  const { mutateAsync: saveApiKey, isPending } = useSaveBostaApiKeyMutation();
  const [apiKey, setApiKey] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const trimmed = apiKey.trim();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (trimmed.length < 8) {
      setFieldError(t(keys.bosta.apiKeyRequired));
      return;
    }
    setFieldError(null);
    setSubmitError(null);
    try {
      await saveApiKey({ storeSlug, apiKey: trimmed });
      toast.success(t(keys.bosta.apiKeySavedNotice));
      setApiKey("");
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : t(keys.errors.generic),
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="mx-auto flex w-full max-w-[520px] flex-col gap-8">
          <p className="text-sm leading-relaxed text-[color:var(--text-secondary)]">
            {t(keys.bosta.setupIntro)}
          </p>

          {submitError ? <Alert message={submitError} /> : null}

          <SetupStep
            label={t(keys.bosta.setupStep1Label)}
            title={t(keys.bosta.setupStep1Title)}
            body={t(keys.bosta.setupStep1Body)}
          >
            <div className="flex flex-col gap-4">
              <OpenBostaLink
                href={BOSTA_API_INTEGRATION_URL}
                label={t(keys.bosta.openBostaAction)}
              />
              <FormField
                label={t(keys.bosta.apiKeyLabel)}
                name="bostaApiKey"
                error={fieldError ?? undefined}
              >
                <TextInput
                  name="bostaApiKey"
                  type="password"
                  value={apiKey}
                  placeholder={t(keys.bosta.apiKeyPlaceholder)}
                  hasError={Boolean(fieldError)}
                  autoComplete="off"
                  autoFocus
                  onChange={(event) => {
                    setApiKey(event.target.value);
                    if (fieldError) setFieldError(null);
                  }}
                />
              </FormField>
            </div>
          </SetupStep>

          <SetupStep
            label={t(keys.bosta.setupStep2Label)}
            title={t(keys.bosta.setupStep2Title)}
            body={t(keys.bosta.setupStep2Body)}
          >
            <OpenBostaLink
              href={BOSTA_PICKUP_LOCATIONS_URL}
              label={t(keys.bosta.setupStep2Action)}
            />
          </SetupStep>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-end border-t border-[color:var(--border-light)] px-4 py-3 sm:px-6">
        <Button
          type="submit"
          variant="primary"
          disabled={isPending || trimmed.length === 0}
          className="h-10 rounded-lg px-6 text-sm font-semibold disabled:opacity-60"
        >
          {isPending ? t(keys.common.loading) : t(keys.bosta.saveApiKeyAction)}
        </Button>
      </div>
    </form>
  );
}

function SetupStep({
  label,
  title,
  body,
  children,
}: {
  label: string;
  title: string;
  body: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <span className="inline-flex h-5 w-fit items-center rounded-md bg-[color:var(--primary-50)] px-2 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--primary-600)]">
        {label}
      </span>
      <h4 className="text-base font-semibold leading-snug text-[color:var(--text-primary)]">
        {title}
      </h4>
      <p className="text-sm leading-relaxed text-[color:var(--text-secondary)]">
        {body}
      </p>
      <div className="mt-1">{children}</div>
    </section>
  );
}

function OpenBostaLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-10 w-fit items-center gap-2 rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 text-sm font-semibold text-[color:var(--text-primary)] transition hover:border-[color:var(--primary-300)] hover:bg-[color:var(--surface-alt)]"
    >
      {label}
      <ExternalLinkIcon />
    </a>
  );
}

/* ----------------------------- Connected view ---------------------------- */

function ConnectedView({
  storeSlug,
  connection,
  onDisconnected,
  t,
}: {
  storeSlug: string;
  connection: BostaConnectionView;
  onDisconnected: () => void;
  t: T;
}) {
  const toast = useToast();
  const locale = useLocale();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { mutateAsync: disconnect, isPending: isDisconnecting } =
    useDisconnectBostaMutation();
  const { mutateAsync: rotateWebhook, isPending: isRotating } =
    useRotateBostaWebhookMutation();
  const [confirmingRotate, setConfirmingRotate] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- clear stale error when the connection's API key changes
    setSubmitError(null);
  }, [connection.apiKeyLast4]);

  const handleRotateWebhook = async () => {
    setSubmitError(null);
    try {
      await rotateWebhook({ storeSlug });
      toast.success(t(keys.bosta.rotateWebhookNotice));
      setConfirmingRotate(false);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : t(keys.errors.generic),
      );
    }
  };

  const updatedLabel = useMemo(() => {
    if (!connection.updatedAt) return null;
    const when = formatRelativeDate(connection.updatedAt, locale);
    if (!when) return null;
    return t(keys.bosta.updatedAt, { when });
  }, [connection.updatedAt, locale, t]);

  const handleCopy = async (value: string) => {
    const ok = await copyTextToClipboard(value);
    if (ok) toast.success(t(keys.bosta.copiedNotice));
  };

  const handleDisconnect = async () => {
    setSubmitError(null);
    try {
      await disconnect({ storeSlug });
      toast.success(t(keys.bosta.disconnectedNotice));
      setConfirmingDisconnect(false);
      onDisconnected();
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : t(keys.errors.generic),
      );
    }
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="mx-auto flex w-full max-w-[520px] flex-col gap-8">
          <StatusRow
            t={t}
            isActive={connection.isActive}
            last4={connection.apiKeyLast4}
            updatedLabel={updatedLabel}
          />

          {submitError ? <Alert message={submitError} /> : null}

          <SectionBlock
            title={t(keys.bosta.webhookSectionTitle)}
            body={t(keys.bosta.webhookSectionBody)}
          >
            <ReadOnlyField
              label={t(keys.bosta.webhookUrlLabel)}
              value={connection.webhookUrl}
              onCopy={handleCopy}
              copyLabel={t(keys.bosta.copyAction)}
            />
            <ReadOnlyField
              label={t(keys.bosta.webhookHeaderNameLabel)}
              value={connection.webhookHeaderName}
              onCopy={handleCopy}
              copyLabel={t(keys.bosta.copyAction)}
            />
            <SecretField
              label={t(keys.bosta.webhookSecretLabel)}
              value={connection.webhookSecret}
              onCopy={handleCopy}
              copyLabel={t(keys.bosta.copyAction)}
              revealLabel={t(keys.bosta.revealSecretAction)}
              hideLabel={t(keys.bosta.hideSecretAction)}
              onRotate={() => setConfirmingRotate(true)}
              rotateLabel={t(keys.bosta.rotateWebhookAction)}
              rotateDisabled={isRotating || confirmingRotate}
            />
            {confirmingRotate ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                <span className="flex-1">
                  {t(keys.bosta.rotateWebhookConfirmBody)}
                </span>
                <div className="flex shrink-0 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isRotating}
                    onClick={() => setConfirmingRotate(false)}
                    className="px-3"
                  >
                    {t(keys.bosta.cancelAction)}
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={isRotating}
                    onClick={() => void handleRotateWebhook()}
                    className="px-3"
                  >
                    {isRotating
                      ? t(keys.common.loading)
                      : t(keys.bosta.rotateWebhookConfirmCta)}
                  </Button>
                </div>
              </div>
            ) : null}
          </SectionBlock>

          <SectionBlock
            title={t(keys.bosta.apiKeySectionTitle)}
            body={t(keys.bosta.apiKeySectionBody)}
          >
            <UpdateApiKeyInline storeSlug={storeSlug} t={t} />
          </SectionBlock>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[color:var(--border-light)] px-4 py-3 sm:px-6">
        {confirmingDisconnect ? (
          <div className="flex flex-1 items-center justify-between gap-3">
            <span className="text-xs text-[color:var(--text-secondary)]">
              {t(keys.bosta.disconnectConfirmBody)}
            </span>
            <div className="flex shrink-0 gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={isDisconnecting}
                onClick={() => setConfirmingDisconnect(false)}
                className="h-10 rounded-lg px-4 text-sm font-semibold disabled:opacity-60"
              >
                {t(keys.bosta.cancelAction)}
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={isDisconnecting}
                onClick={() => void handleDisconnect()}
                className="h-10 rounded-lg px-4 text-sm font-semibold disabled:opacity-60"
              >
                {isDisconnecting
                  ? t(keys.common.loading)
                  : t(keys.bosta.disconnectConfirmCta)}
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingDisconnect(true)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--error-500)] transition hover:text-[color:var(--error-600,_#8B2525)]"
          >
            <UnlinkIcon />
            {t(keys.bosta.disconnectAction)}
          </button>
        )}
      </div>
    </>
  );
}

function StatusRow({
  t,
  isActive,
  last4,
  updatedLabel,
}: {
  t: T;
  isActive: boolean;
  last4: string;
  updatedLabel: string | null;
}) {
  const pillClass = isActive
    ? "bg-[color:var(--success-50,#E8F7EE)] text-[color:var(--success-700,#116733)]"
    : "bg-amber-50 text-amber-800";
  const dotClass = isActive
    ? "bg-[color:var(--success-500,#16A34A)]"
    : "bg-amber-500";
  const label = isActive
    ? t(keys.bosta.statusConnected)
    : t(keys.bosta.statusPaused);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${pillClass}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
        {label}
      </span>
      <span className="text-xs text-[color:var(--text-secondary)]">
        {t(keys.bosta.connectedSummary, { last4 })}
      </span>
      {updatedLabel ? (
        <>
          <span className="text-xs text-[color:var(--text-muted)]">•</span>
          <span className="text-xs text-[color:var(--text-muted)]">
            {updatedLabel}
          </span>
        </>
      ) : null}
    </div>
  );
}

function SectionBlock({
  title,
  body,
  action,
  children,
}: {
  title: string;
  body: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-semibold text-[color:var(--text-primary)]">
            {title}
          </h4>
          <p className="text-xs leading-relaxed text-[color:var(--text-muted)]">
            {body}
          </p>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

function UpdateApiKeyInline({ storeSlug, t }: { storeSlug: string; t: T }) {
  const toast = useToast();
  const { mutateAsync: saveApiKey, isPending } = useSaveBostaApiKeyMutation();
  const [value, setValue] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const trimmed = value.trim();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (trimmed.length < 8) {
      setFieldError(t(keys.bosta.apiKeyRequired));
      return;
    }
    setFieldError(null);
    try {
      await saveApiKey({ storeSlug, apiKey: trimmed });
      toast.success(t(keys.bosta.apiKeySavedNotice));
      setValue("");
    } catch (error) {
      setFieldError(
        error instanceof Error ? error.message : t(keys.errors.generic),
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="flex-1">
          <TextInput
            name="bostaApiKeyUpdate"
            type="password"
            value={value}
            placeholder={t(keys.bosta.apiKeyPlaceholder)}
            hasError={Boolean(fieldError)}
            autoComplete="off"
            onChange={(event) => {
              setValue(event.target.value);
              if (fieldError) setFieldError(null);
            }}
          />
          {fieldError ? (
            <p className="mt-1 text-xs text-[color:var(--error-500)]">
              {fieldError}
            </p>
          ) : null}
        </div>
        <Button
          type="submit"
          variant="primary"
          disabled={isPending || trimmed.length === 0}
          className="h-10 shrink-0 rounded-lg px-4 text-sm font-semibold disabled:opacity-60"
        >
          {isPending
            ? t(keys.common.loading)
            : t(keys.bosta.updateApiKeyAction)}
        </Button>
      </div>
    </form>
  );
}

/* ----------------------------- Shared bits ------------------------------ */

function ReadOnlyField({
  label,
  value,
  onCopy,
  copyLabel,
}: {
  label: string;
  value: string;
  onCopy: (value: string) => void;
  copyLabel: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-[color:var(--text-secondary)]">
        {label}
      </label>
      <div className="flex items-stretch gap-0 overflow-hidden rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface-alt)]">
        <code
          className="flex-1 truncate px-3 py-2.5 font-mono text-xs text-[color:var(--text-primary)]"
          dir="ltr"
        >
          {value}
        </code>
        <button
          type="button"
          onClick={() => onCopy(value)}
          aria-label={copyLabel}
          className="flex shrink-0 items-center justify-center gap-1.5 border-s border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-xs font-semibold text-[color:var(--text-secondary)] transition hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text-primary)]"
        >
          <CopyIcon />
          <span className="hidden sm:inline">{copyLabel}</span>
        </button>
      </div>
    </div>
  );
}

function SecretField({
  label,
  value,
  onCopy,
  copyLabel,
  revealLabel,
  hideLabel,
  onRotate,
  rotateLabel,
  rotateDisabled,
}: {
  label: string;
  value: string;
  onCopy: (value: string) => void;
  copyLabel: string;
  revealLabel: string;
  hideLabel: string;
  onRotate?: () => void;
  rotateLabel?: string;
  rotateDisabled?: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  const display = revealed ? value : "•".repeat(Math.min(value.length, 40));
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-[color:var(--text-secondary)]">
        {label}
      </label>
      <div className="flex items-stretch gap-0 overflow-hidden rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface-alt)]">
        <code
          className="flex-1 truncate px-3 py-2.5 font-mono text-xs text-[color:var(--text-primary)]"
          dir="ltr"
        >
          {display}
        </code>
        {onRotate ? (
          <button
            type="button"
            onClick={onRotate}
            disabled={rotateDisabled}
            aria-label={rotateLabel ?? "Rotate"}
            title={rotateLabel}
            className="flex shrink-0 items-center justify-center border-s border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-[color:var(--text-secondary)] transition hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshIcon />
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setRevealed((v) => !v)}
          aria-label={revealed ? hideLabel : revealLabel}
          className="flex shrink-0 items-center justify-center border-s border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-[color:var(--text-secondary)] transition hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text-primary)]"
        >
          <EyeIcon hidden={!revealed} />
        </button>
        <button
          type="button"
          onClick={() => onCopy(value)}
          aria-label={copyLabel}
          className="flex shrink-0 items-center justify-center gap-1.5 border-s border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-xs font-semibold text-[color:var(--text-secondary)] transition hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text-primary)]"
        >
          <CopyIcon />
          <span className="hidden sm:inline">{copyLabel}</span>
        </button>
      </div>
    </div>
  );
}

/* ------------------------ Relative date formatting ----------------------- */

function formatRelativeDate(iso: string, locale: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  try {
    const rtf = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: "auto" });
    if (Math.abs(diffMin) < 60) return rtf.format(-diffMin, "minute");
    const diffHours = Math.round(diffMin / 60);
    if (Math.abs(diffHours) < 24) return rtf.format(-diffHours, "hour");
    const diffDays = Math.round(diffHours / 24);
    if (Math.abs(diffDays) < 30) return rtf.format(-diffDays, "day");
    return date.toLocaleDateString(intlLocale(locale), {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return date.toLocaleDateString(intlLocale(locale));
  }
}

/* --------------------------------- Icons -------------------------------- */

function CopyIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="rtl:-scale-x-100"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

function UnlinkIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m18.84 12.25 1.72-1.71h-.02a5.004 5.004 0 0 0-.12-7.07 5.006 5.006 0 0 0-6.95 0l-1.72 1.71" />
      <path d="m5.17 11.75-1.71 1.71a5.004 5.004 0 0 0 .12 7.07 5.006 5.006 0 0 0 6.95 0l1.71-1.71" />
      <line x1="8" y1="2" x2="8" y2="5" />
      <line x1="2" y1="8" x2="5" y2="8" />
      <line x1="16" y1="19" x2="16" y2="22" />
      <line x1="19" y1="16" x2="22" y2="16" />
    </svg>
  );
}

function EyeIcon({ hidden }: { hidden: boolean }) {
  return hidden ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
